import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Engagement, IdentityTokenProfile } from '../../clientModels'
import { PhaseCode } from '../../enums'
import { AppState } from '../../store'
import { VERIFY_SUBMIT_READY_ID } from '../questionList/questionListButtons'
import CchErrorLinks from './cchErrorLinks/cchErrorLinks'

// Fire-time buffer for the PBC "Ready to Submit" ribbon. Mirrors the
// gate on the SUBMIT modal in questionListButtons.tsx -- both react to
// the same successfulQuestions/totalQuestions equality and must not
// light up during the transient window between the local answer-update
// render and httpSaveBeginAction. !isSaving covers the longer window
// (server-side rules + visibility expand BEFORE httpSaveEndAction
// fires); this 300ms timer covers the ~10ms gap before the save round-
// trip begins.
const PBC_COMPLETE_DEBOUNCE_MS = 300

interface EngagementSummaryMessageProps {
  engagement: Engagement
  user?: IdentityTokenProfile
}

export const EngagementSummaryMessage: React.FC<EngagementSummaryMessageProps> = ({
  engagement,
  user,
}) => {
  const isSaving = useSelector(
    (state: AppState) => state.http.saving.length > 0
  )
  // Any answer locally modified but not yet server-confirmed. While the
  // user is editing the last currently-visible question, the count
  // equality (54/54) renders but the save hasn't been dispatched yet --
  // isSaving is still false. Without this gate the ribbon shows during
  // editing and hides as soon as the user blurs and the save kicks off.
  const hasDirtyAnswers = useSelector((state: AppState) => {
    const answers = state.engagementQuestions[engagement.id] || {}
    return Object.values(answers).some(a => !!a && a.isDirty)
  })
  // Set while questionListButtons is running its external-user server-
  // verify refetch. Suppresses this label during the refetch window so
  // it doesn't flash on 990-EZ when the refetch is about to reveal more
  // visible sections and drop pbcCompleteNow back to false.
  const isVerifyingSubmitReady = useSelector((state: AppState) =>
    state.http.loading.includes(VERIFY_SUBMIT_READY_ID)
  )
  const [delayedPbcComplete, setDelayedPbcComplete] = useState<boolean>(false)
  const timerRef = useRef<number | undefined>(undefined)

  const pbcCompleteNow =
    engagement.phase === PhaseCode.PBC &&
    // Require totalQuestions > 0 -- see engagementPhaseService.ts. Prevents
    // the "Once your engagement is submitted..." label from flashing on a
    // freshly-created 990-PF where server-stored count is still 0.
    engagement.totalQuestions > 0 &&
    engagement.successfulQuestions >= engagement.totalQuestions &&
    !isSaving &&
    !hasDirtyAnswers &&
    !isVerifyingSubmitReady

  useEffect(() => {
    if (timerRef.current !== undefined) {
      window.clearTimeout(timerRef.current)
      timerRef.current = undefined
    }

    if (!pbcCompleteNow) {
      if (delayedPbcComplete) {
        setDelayedPbcComplete(false)
      }
      return
    }

    timerRef.current = window.setTimeout(() => {
      timerRef.current = undefined
      setDelayedPbcComplete(true)
    }, PBC_COMPLETE_DEBOUNCE_MS)

    return () => {
      if (timerRef.current !== undefined) {
        window.clearTimeout(timerRef.current)
        timerRef.current = undefined
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pbcCompleteNow])

  if (!user) {
    return null
  }

  const message = user.isExternal
    ? clientUserMessage(engagement, delayedPbcComplete)
    : rsmUserMessage(engagement)

  return message ? (
    <div className='engagement-summary-message'>{message}</div>
  ) : null
}

function clientUserMessage(
  engagement: Engagement,
  delayedPbcComplete: boolean
): string | null {
  const latestTaxIntegrationStatus =
    engagement.taxIntegrationStatuses &&
    engagement.taxIntegrationStatuses
      .filter(x => x.type === 'CCHExport')
      .sort((x, y) => y.id - x.id)[0]

  switch (engagement.phase) {
    case PhaseCode.PBC:
      return delayedPbcComplete
        ? 'Once your engagement is submitted for review you will no longer be able to edit your questions.'
        : null

    case PhaseCode.Review:
      return 'Your information is being reviewed.You will receive an email if any items require your attention.'

    case PhaseCode.CCH:
      return !latestTaxIntegrationStatus ||
        latestTaxIntegrationStatus.status === 'IN PROGRESS' ||
        latestTaxIntegrationStatus.status === 'TSAPROCESSING'
        ? 'ENGAGEMENT SUBMISSION IN PROGRESS...'
        : null

    default:
      return null
  }
}

function rsmUserMessage(engagement: Engagement): React.ReactNode {
  const latestExportTaxIntegrationStatus =
    engagement.taxIntegrationStatuses &&
    engagement.taxIntegrationStatuses
      .filter(x => x.type === 'CCHExport')
      .sort((x, y) => y.id - x.id)[0]
  const latestImportTaxIntegrationStatus =
    engagement.taxIntegrationStatuses &&
    engagement.taxIntegrationStatuses
      .filter(x => x.type === 'CCHImport')
      .sort((x, y) => y.id - x.id)[0]
  switch (engagement.phase) {
    case PhaseCode.CCH:
      if (
        !latestExportTaxIntegrationStatus ||
        (latestExportTaxIntegrationStatus &&
          (latestExportTaxIntegrationStatus.status === 'IN PROGRESS' ||
            latestExportTaxIntegrationStatus.status === 'TSAPROCESSING'))
      ) {
        return (
          <div className='loading'>ENGAGEMENT SUBMISSION IN PROGRESS</div>
        )
      } else if (
        latestExportTaxIntegrationStatus &&
        latestExportTaxIntegrationStatus.status === 'SUCCESS'
      ) {
        return (
          <div className='submission-success'>
            <div className='submission-success-heading'>
              ENGAGEMENT SUBMITTED
            </div>
          </div>
        )
      } else if (
        latestExportTaxIntegrationStatus &&
        latestExportTaxIntegrationStatus.status === 'PARTIAL SUCCESS'
      ) {
        return (
          <div className='submission-failed'>
            <CchErrorLinks
              engagementId={engagement?.id}
              headerMessage={'ENGAGEMENT SUBMITTED WITH ISSUES'}
              message={latestExportTaxIntegrationStatus.message}
              messageClassName='submission-failed-message'
            />
          </div>
        )
      } else if (
        latestExportTaxIntegrationStatus &&
        latestExportTaxIntegrationStatus.status === 'ERROR'
      ) {
        return (
          <div className='submission-failed'>
            <CchErrorLinks
              headerMessage={'ENGAGEMENT SUBMISSION FAILED:'}
              engagementId={engagement?.id}
              message={latestExportTaxIntegrationStatus.message}
              messageClassName='submission-failed-message'
            />
          </div>
        )
      }
      return null

    default:
      if (
        latestImportTaxIntegrationStatus &&
        (latestImportTaxIntegrationStatus.status === 'IN PROGRESS' ||
          latestImportTaxIntegrationStatus.status === 'TSAPROCESSING')
      ) {
        return <div className='loading'>RETRIEVING LAST YEAR'S DATA</div>
      }
      if (
        latestImportTaxIntegrationStatus &&
        latestImportTaxIntegrationStatus.status === 'SUCCESS'
      ) {
        return (
          <div className='submission-success'>
            <div className='submission-success-heading'>
              LAST YEAR'S DATA RETRIEVED:
            </div>{' '}
            Data from {engagement.priorYearReturnId} successfully populated
            into all questions.
          </div>
        )
      }
      if (
        latestImportTaxIntegrationStatus &&
        latestImportTaxIntegrationStatus.status === 'PARTIAL SUCCESS'
      ) {
        return (
          <div className='submission-failed'>
            <CchErrorLinks
              engagementId={engagement?.id}
              message={latestImportTaxIntegrationStatus.message}
              headerMessage={"LAST YEAR'S DATA RETRIEVED:"}
              subHeaderMessage={`Data from ${engagement.priorYearReturnId} successfully populated some questions.`}
              messageClassName='submission-failed-message'
            />
          </div>
        )
      }
      if (
        latestImportTaxIntegrationStatus &&
        latestImportTaxIntegrationStatus.status === 'ERROR'
      ) {
        return (
          <div className='submission-failed'>
            <CchErrorLinks
              headerMessage={"UNABLE TO RETRIEVE LAST YEAR'S DATA:"}
              engagementId={engagement?.id}
              message={latestImportTaxIntegrationStatus.message}
              messageClassName='submission-failed-message'
            />
          </div>
        )
      }
      return null
  }
}
